import React from "react";
import styled from "styled-components/macro";
import CloseIcon from "@mui/icons-material/Close";
import { Button } from "@mui/material";
import { Trans } from "react-i18next";
import { devicePaddings } from "../../tools/device";
import { useSubscriptions } from "../../root/profile/subscription/subscriptionHooks";
import { useStoreIds } from "../../root/profile/subscription/subscriptionIds";
import AdPaywallComparisonGrid from "./AdPaywallComparisonGrid";
import BlackFridayBackground from "../../icons/BlackFridayBackground";

const { top, bottom } = devicePaddings();

const BlackFridayWrap = styled.div`
  overflow: hidden;
  width: 100%;
  height: 100%;
  position: relative;
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-width: ${({ theme }) => theme.duo.maxWidth.narrow};
  margin: 0 auto;
  padding: ${top + 20}px 20px ${bottom + 20}px;
  height: 100%;
  width: 100%;
  background: transparent;
  position: relative;
  color: white;
  z-index: 1;
`;

const Header = styled.header`
  display: flex;
  justify-content: flex-end;
`;

const CloseButton = styled.button<{ $isVisible: boolean }>`
  min-width: 24px;
  width: 24px;
  height: 24px;
  padding: 0;
  background: none;
  border: none;
  color: ${({ theme }) => theme.duo.palette.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $isVisible }) => ($isVisible ? 0.3 : 0)};
  transition: opacity 2.5s;
  pointer-events: ${({ $isVisible }) => ($isVisible ? "auto" : "none")};
  &:hover {
    opacity: 1;
  }
  svg {
    font-size: 20px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-family: "Noto Sans", "Segoe UI", sans-serif;
  color: ${({ theme }) => theme.duo.palette.white};
  margin: 0 0 15px;
  text-align: center;
`;

const BoldText = styled.span`
  font-weight: 700;
`;

const TitleSection = styled.div`
  text-align: center;
`;

const ButtonContainer = styled.div`
  width: 100%;
  text-align: center;
  margin-top: 20px;
`;

const TrialButton = styled(Button)`
  && {
    background-color: ${({ theme }) => theme.duo.palette.greenMedium};
    color: ${({ theme }) => theme.duo.palette.white};
    text-transform: uppercase;
    font-family: Roboto, "Segoe UI", sans-serif;
    font-weight: ${({ theme }) => theme.duo.fontWeight.w700};
    font-size: 14px;
    padding: 11px 20px;
    border-radius: 100px;
    margin-bottom: 10px;
    white-space: nowrap;
  }
`;

const TrialInfo = styled.div`
  font-size: ${({ theme }) => theme.duo.fontSize.small};
  margin: 5px 0;
`;

const BlackFridayTitle = styled.span`
  font-size: 60px;
  font-family: "Noto Sans", "Segoe UI", sans-serif;
  color: transparent;
  -webkit-text-stroke: 2px white;
  margin: 0 0 15px;
  text-align: center;
  text-transform: uppercase;
  font-weight: 700;
  line-height: 1;
  @media (max-width: 375px) {
    font-size: 40px;
  }
`;

const BlackFridaySubtitle = styled.div`
  font-size: 60px;
  font-family: "Noto Sans", "Segoe UI", sans-serif;
  color: white;
  margin: 0 0 15px;
  text-align: center;
  text-transform: uppercase;
  font-weight: 700;
  line-height: 1.2;
  @media (max-width: 375px) {
    font-size: 40px;
  }
`;

const GreyedOutText = styled.span`
  color: ${({ theme }) => theme.duo.color.textGrey};
  display: inline-block;
  margin-top: 5px;
`;

const DescriptionText = styled.span`
  font-size: 20px;
  margin-top: 10px;
  text-align: center;
`;

const ButtonContainerTop = styled.div`
  font-size: 14px;
  margin-bottom: 10px;
  text-align: center;
`;

type Props = {
  onClose: VoidFunction;
  isCloseVisible: boolean;
  onSubscribe: (e: React.MouseEvent) => void;
};

const InternalAdPaywallBlackFriday: React.FC<Props> = ({ onClose, isCloseVisible, onSubscribe }) => {
  const { subscriptions } = useSubscriptions();
  const { storeIdYear } = useStoreIds();

  // Calculate full price from discounted price for BlackFriday (60% off)
  const calculateFullPrice = (discountedPrice: string) => {
    const match = discountedPrice.match(/^([^\d]*)([\d,.]+)([^\d]*)$/);
    if (!match) return discountedPrice;

    const [, prefix, numericStr, suffix] = match;
    const numericValue = parseFloat(numericStr.replace(",", "."));
    const fullValue = numericValue / 0.4; // 60% off = 40% of original

    const decimalPlaces = numericStr.includes(",")
      ? numericStr.split(",")[1]?.length || 0
      : numericStr.split(".")[1]?.length || 0;

    const formattedValue = decimalPlaces > 0 ? fullValue.toFixed(decimalPlaces) : Math.round(fullValue).toString();
    const finalValue = numericStr.includes(",") ? formattedValue.replace(".", ",") : formattedValue;

    return `${prefix}${finalValue}${suffix}`;
  };

  return (
    <BlackFridayWrap>
      <BlackFridayBackground />
      <Wrap>
        <Header>
          <CloseButton onClick={onClose} aria-label="Close premium offer" $isVisible={isCloseVisible}>
            <CloseIcon />
          </CloseButton>
        </Header>

        <TitleSection>
          <Title>
            <BlackFridayTitle>Black Friday</BlackFridayTitle>
            <BlackFridaySubtitle>-60%</BlackFridaySubtitle>
          </Title>
        </TitleSection>

        <DescriptionText>
          <Trans>
            Get more from your <BoldText>study&nbsp;plan!</BoldText>
          </Trans>
        </DescriptionText>

        <AdPaywallComparisonGrid />

        <ButtonContainer>
          <ButtonContainerTop>
            <Trans>Expires soon!</Trans>
          </ButtonContainerTop>
          <TrialButton variant="contained" fullWidth onClick={onSubscribe} disabled={!subscriptions}>
            <Trans>Get 60% off!</Trans>
          </TrialButton>
          {subscriptions && (
            <TrialInfo>
              {subscriptions[storeIdYear]?.price} / <Trans>1 Year</Trans>
              <br />
              <GreyedOutText>
                <Trans>Previously</Trans> {calculateFullPrice(subscriptions[storeIdYear]?.price || "")}
              </GreyedOutText>
            </TrialInfo>
          )}
        </ButtonContainer>
      </Wrap>
    </BlackFridayWrap>
  );
};

export default InternalAdPaywallBlackFriday;
